/**
 * Header Component
 * Navigation header with user menu and responsive hamburger menu
 */

import { useState, useEffect, useRef } from 'react';
import { hasAdminAccess } from '../../lib/roles';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu } from '@headlessui/react';
import {
  KeyRound,
  Link as LinkIcon,
  LogOut,
  ScrollText,
  Menu as MenuIcon,
  X,
  Home,
  Users,
  Puzzle,
  Building2,
  Gamepad2,
  LayoutDashboard,
  UserCog,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { getInitials, getInitialsFromEmail } from '../../lib/utils';
import { ThemeToggle } from './ThemeToggle';

export const Header = () => {
  const { user, member, isAuthenticated, logout, isLoggingOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  const handleLogout = async () => {
    setIsMobileMenuOpen(false);
    await logout();
    navigate('/login');
  };

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close on Escape key
  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMobileMenuOpen]);

  // Close on resize above lg breakpoint
  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  // Body scroll lock when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  if (!isAuthenticated) return null;

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  const navLinks = [
    { to: '/home', label: 'Home', icon: Home },
    { to: '/members', label: 'Members', icon: Users },
    { to: '/teams', label: 'Teams', icon: Puzzle },
    { to: '/departments', label: 'Departments', icon: Building2 },
    { to: '/quiz', label: 'Quiz', icon: Gamepad2 },
  ];

  const adminLinks = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/users', label: 'Users', icon: UserCog },
  ];

  return (
    <>
      <header className="bg-surface shadow-sm border-b border-[var(--border-default)] relative z-30">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Hamburger (mobile/tablet) + Logo (desktop) */}
            <div className="flex items-center space-x-8">
              {/* Hamburger button — visible below lg */}
              <button
                className="lg:hidden p-2 rounded-md text-body hover:bg-surface-hover transition-colors"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <MenuIcon className="h-6 w-6" />
                )}
              </button>

              {/* Logo — hidden below lg */}
              <Link
                to="/home"
                className="hidden lg:flex items-center"
              >
                <img
                  src="/PSG logo transparent.png"
                  alt="Team Wizard"
                  className="h-10 w-auto"
                />
              </Link>

              {/* Desktop nav links — hidden below lg */}
              <nav className="hidden lg:flex space-x-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive(link.to)
                        ? 'text-blue-600 dark:text-blue-400 bg-surface-hover'
                        : 'text-body hover:text-blue-600 dark:hover:text-blue-400 hover:bg-surface-hover'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                {hasAdminAccess(user?.role) &&
                  adminLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        isActive(link.to)
                          ? 'text-blue-600 dark:text-blue-400 bg-surface-hover'
                          : 'text-body hover:text-blue-600 dark:hover:text-blue-400 hover:bg-surface-hover'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
              </nav>
            </div>

            {/* User Menu Dropdown */}
            <Menu as="div" className="relative">
              <Menu.Button className="h-9 w-9 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center text-white text-sm font-medium cursor-pointer hover:ring-2 hover:ring-gray-300 dark:hover:ring-gray-600 hover:ring-offset-1 transition-all">
                {member?.first_name || member?.last_name
                  ? getInitials(member.first_name, member.last_name)
                  : getInitialsFromEmail(user?.email)}
              </Menu.Button>

              <Menu.Items className="absolute right-0 mt-2 w-64 origin-top-right bg-surface-elevated rounded-lg shadow-lg ring-1 ring-black/5 dark:ring-white/10 focus:outline-none z-50">
                {/* User Info */}
                <div className="px-4 py-3">
                  <p className="text-sm font-medium text-heading truncate">
                    {member?.first_name || member?.last_name
                      ? `${member.first_name ?? ''} ${member.last_name ?? ''}`.trim()
                      : user?.email}
                  </p>
                  <p className="text-xs text-meta capitalize">
                    {user?.role || 'User'}
                  </p>
                </div>

                {/* Separator */}
                <div className="border-t border-[var(--border-default)]" />

                {/* Menu Items */}
                <div className="py-1">
                  {/* Theme Toggle — visible to all users */}
                  <ThemeToggle />

                  {hasAdminAccess(user?.role) && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => navigate('/groups')}
                          className={`${
                            active ? 'bg-surface-hover' : ''
                          } w-full flex items-center px-4 py-2 text-sm text-body`}
                        >
                          <LinkIcon className="h-icon-sm w-icon-sm mr-3 text-meta" />
                          Groups
                        </button>
                      )}
                    </Menu.Item>
                  )}

                  {hasAdminAccess(user?.role) && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => navigate('/audit-logs')}
                          className={`${
                            active ? 'bg-surface-hover' : ''
                          } w-full flex items-center px-4 py-2 text-sm text-body`}
                        >
                          <ScrollText className="h-icon-sm w-icon-sm mr-3 text-meta" />
                          Audit Logging
                        </button>
                      )}
                    </Menu.Item>
                  )}
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => navigate('/change-password')}
                        className={`${
                          active ? 'bg-surface-hover' : ''
                        } w-full flex items-center px-4 py-2 text-sm text-body`}
                      >
                        <KeyRound className="h-icon-sm w-icon-sm mr-3 text-meta" />
                        Change Password
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleLogout}
                        disabled={isLoggingOut}
                        className={`${
                          active ? 'bg-surface-hover' : ''
                        } w-full flex items-center px-4 py-2 text-sm text-body disabled:opacity-50`}
                      >
                        <LogOut className="h-icon-sm w-icon-sm mr-3 text-meta" />
                        {isLoggingOut ? 'Logging out...' : 'Log out'}
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Menu>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay — only rendered below lg */}
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/30 z-40 transition-opacity duration-300 lg:hidden ${
          isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
        aria-hidden="true"
      />

      {/* Slide-in menu */}
      <div
        ref={mobileMenuRef}
        className={`fixed top-0 left-0 h-full z-50 bg-surface transition-transform duration-300 ease-in-out lg:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ width: '16rem' }}
      >
        {/* Close button */}
        <div className="flex items-center justify-start h-16 px-4">
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="p-2 rounded-md text-body hover:bg-surface-hover transition-colors"
            aria-label="Close menu"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Separator */}
        <div className="mx-4 border-b border-[var(--border-default)]" />

        {/* Navigation Links */}
        <nav className="py-2">
          {navLinks.map((link) => {
            const Icon = link.icon;
            return (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center px-4 py-3 text-base font-medium transition-colors ${
                  isActive(link.to)
                    ? 'text-blue-600 dark:text-blue-400 bg-surface-hover'
                    : 'text-body hover:bg-surface-hover'
                }`}
              >
                <Icon className="h-6 w-6 mr-4 text-blue-600 dark:text-blue-400" />
                {link.label}
              </Link>
            );
          })}

          {/* Admin section */}
          {hasAdminAccess(user?.role) && (
            <>
              <div className="mx-4 my-2 border-b border-[var(--border-default)]" />
              {adminLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center px-4 py-3 text-base font-medium transition-colors ${
                      isActive(link.to)
                        ? 'text-blue-600 dark:text-blue-400 bg-surface-hover'
                        : 'text-body hover:bg-surface-hover'
                    }`}
                  >
                    <Icon className="h-6 w-6 mr-4 text-blue-600 dark:text-blue-400" />
                    {link.label}
                  </Link>
                );
              })}
            </>
          )}
        </nav>
      </div>
    </>
  );
};
